/* eslint-disable max-lines */
import type {
  HardwareDeviceResponse,
  LinkHardwareDeviceRequest,
  LogoutEverywhereResponse,
  UnlinkHardwareDeviceResponse,
  UpdateHardwareDeviceRequest,
} from '@/types';
import {
  type AssignAppleMigrationTicketResponse,
  type BuildCustomTemplateRequest,
  type DetermineLoginResponse,
  type IntegrationRequest,
  type NoteStructureOptionsResponse,
} from '@/types';
import { type DictationContextResponse } from '@/types/dictation-context.types';
import { customFetchEventSource } from '@/utils/fetchEventSource';
import { wasRetriedByAuthHandler } from '@/utils/http/authRetry';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { logger } from '@/utils/logger/logger';
import {
  identityBrokerHttp,
  identityBrokerPublicHttp,
  scribeHttp,
  scribeHttpV2,
} from '@/utils/scribeHTTP';
import { extractRegionFromAccessToken } from '@heidi/auth/tokens';
import {
  type RecordPatientSummaryDismissalRequest,
  type RecordPatientSummaryDismissalResponse,
} from '@heidi/patients/contracts';
import { type operations, Region } from '@heidi/schema/ml-scribe';
import { type TemplateResponse } from '@heidi/templates/contracts';
import { type UserResponse } from '@heidi/workspace/contracts';
import type { AxiosError } from 'axios';
import { isNetworkOrIdempotentRequestError } from 'axios-retry';

/**
 * App-facing sort vocabulary for the organization template table.
 *
 * NOT the schema's `TemplateSortBy`, despite the name: this is a narrower
 * subset with different member names (`CREATED_AT`/`ALPHABETICAL` vs
 * `CREATED_TIME`/`NAME`) mapped onto the same wire values. It deliberately
 * stayed behind at the ARCFE-1012 carve — the templates endpoints bind the
 * SCHEMA enum, so moving this one would have silently changed their
 * signatures.
 */
export enum TemplateSortBy {
  CREATED_AT = 'CREATED_TIME',
  LAST_USED = 'LAST_USED',
  ALPHABETICAL = 'NAME',
}

// export type CreateTemplatePayload = {
//   name: string;
//   description: string;
//   structure_template: string;
//   template_html: string;
//   is_private: boolean;
//   template_category?: TemplateCategory;
//   template_visibility?: string;
//   team_id?: string;
//   trigger: 'from_scratch';
// };

/** @domain workspace */
export const deleteMyAccount = (reason?: string, details?: string) => {
  return scribeHttp.delete<UserResponse>(`/user/`, {
    data: {
      reason,
      details,
    },
  });
};

// Moved to `@heidi/session/data` with the D1 family. The door names it
// `getSessionsV1` (the v2 fn owns `getSessions` there); this alias keeps the
// original v1 name for every existing importer + `jest.mock` literal.
export { getSessionsV1 as getSessions } from '@heidi/session/data';

// Moved to `@heidi/session/data` with the D3 family; re-exported so existing
// importers + `jest.mock` literals keep resolving until Z1's codemod.
export { getSessionById } from '@heidi/session/data';

// Carved to `@heidi/session/data` with the warm pair (ARCFE-991); name-intact
// re-export so existing importers + `jest.mock` literals keep resolving.
export { updateSessionById } from '@heidi/session/data';
// Carved to `@heidi/session/data` at D5 (ARCFE-990); name-intact re-exports.
export { getDocumentById, getDocumentsBySessionId } from '@heidi/session/data';

// Carved to `@heidi/session/data` at ARCFE-1004; name-intact re-export.
export { deleteDocumentById } from '@heidi/session/data';

// TRANSFERRED to `@heidi/commerce/data` — billing-portal session (MONEY-845).
export type {
  BillingPortalLinkParams,
  BillingPortalQueryParams,
} from '@heidi/commerce/contracts';
export { getStripeLink } from '@heidi/commerce/data';

// moved to v2
// export const postCreateEmptyDocument = ({
//   sessionId,
//   templateId,
//   question,
//   generationType,
// }: {
//   sessionId: string;
//   templateId?: string;
//   question?: string;
//   generationType: DocumentGenType;
// }) => {
//   return scribeHttp.post<DocumentResponse>(`/sessions/${sessionId}/document/create`, {
//     generation_type: generationType,
//     question,
//     template_id: templateId,
//   });
// };

// moved to v2
// export const generateDocument = ({
//   sessionId,
//   documentId,
// }: {
//   sessionId: string;
//   documentId?: string;
// }) => {
//   return scribeHttp.post<string>(`/sessions/${sessionId}/document/${documentId}`);
// };

// Carved to `@heidi/session/data` at ARCFE-1004; name-intact re-export.
export { updateDocument } from '@heidi/session/data';

/** @domain templates */
export const getTemplates = (sortBy?: TemplateSortBy) => {
  return scribeHttp.get<TemplateResponse[]>(`/template/`, {
    params: { sort_by: sortBy },
  });
};

// build a template from generated document
export const buildTemplateFromDocument = (params: BuildCustomTemplateRequest) => {
  return customFetchEventSource({
    abortController: new AbortController(),
    endPointVersion: 'v2-1',
    path: 'templates/build',

    requestBody: {
      ...params,
    },
  });

  // return scribeHttp.post<string>(`/template/build`, { ...params });
};

// Carved to `@heidi/session/data` with the D4 family (the request type moved
// beside its fn, per the merge-cluster precedent).
export {
  shareConsultNoteByEmail,
  type ShareConsultNoteRecipientsRequest,
} from '@heidi/session/data';

// `deleteSessionById` (`DELETE /sessions/{id}`) was DELETED at ARCFE-1096.
// The ARCFE-1060 arm widening made it visible (the family's `apiHubs`
// symbolPattern had demanded a capital-D `Delete…`), and re-measured at HEAD
// it still had exactly two references: its own declaration and the
// `src/pages/api/scribeAPI.ts` compat re-export. No hook, no component, no
// test, no namespace import of either barrel — the live single-session path
// is the batch `deleteSessionByIds`, carved long ago. Precedent for a
// zero-consumer hub endpoint is DELETE RATHER THAN CARVE
// (`deleteAllSessionClinicalTasks`, re-applied to five calls endpoints at
// ARCFE-1038), so the family's `api` claim empties by shrinking the hub
// rather than by growing the package.

// Carved to `@heidi/session/data` with the D2 transcript family; re-exported
// so existing importers and jest.mock literals keep resolving (deleted at Z2).
export { deleteSessionTranscript } from '@heidi/session/data';

// Carved to `@heidi/session/data` with the D6 family (see the transcript
// carve above); the consuming mutation stays app-side and resolves through
// this re-export.
export { deleteSessionByIds } from '@heidi/session/data';

// export const createTemplate = (data: CreateTemplatePayload) => {
//   return scribeHttp.post<TemplateResponse>(`/template/`, data);
// };

// export const editTemplate = (template: {
//   name: string;
//   structure_template: string;
//   id: string;
//   template_category?: TemplateCategory;
// }) => {
//   return scribeHttp.patch<TemplateResponse>(`/template/${template.id}`, {
//     name: template.name,
//     structure_template: template.structure_template,
//     template_category: template.template_category,
//   });
// };

/** @domain REVIEW */
export const getNoteStructureBySpecialtyId = (specialtyId: string) => {
  return scribeHttp.get<NoteStructureOptionsResponse>(
    `/specialties/${specialtyId}/note-structure-options`
  );
};

// Carved to `@heidi/workspace/data` at D1 (ARCFE-1037) — the five user-record
// write endpoints (with `createUser`/`toggleUserMfa`/`userSelfOnboard` leaving
// `scribeAPIV2.ts` in the same carve), name-intact re-exports so every
// existing importer and `jest.mock` literal keeps resolving. Transport is
// unchanged: the package fns resolve this module's own `scribeHttp` singleton
// through the `@heidi/api/client` registry. A suite that mocks
// `@/utils/scribeHTTP` no longer intercepts these — it must ALSO set a
// registry double (`setScribeHttpClients`), see `src/__tests__/api/scribeAPI.test.ts`.
// `getUser` joined them at ARCFE-1141 — the plain `/user/` record read that D1
// left behind because its scope was writes only. Same client, same route
// including the trailing slash, name-intact.
export {
  getUser,
  updateUser,
  updateUserName,
  updateUserPendingOnboardingSubtasks,
  updateUserPendingOnboardingTasks,
  updateUserRegion,
} from '@heidi/workspace/data';

/**
 * Atomically record a patient-summary tab dismissal for the current session. The
 * server owns the distinct-session count / dedupe / seen logic and returns the
 * updated state plus whether the turn-off prompt should be shown.
 * @domain workspace
 */
export const recordPatientSummaryTurnOffPromptDismissal = (
  input: RecordPatientSummaryDismissalRequest
) => {
  return scribeHttp.post<RecordPatientSummaryDismissalResponse>(
    '/user/patient-summary-turn-off-prompt:record-dismissal',
    input
  );
};

// Carved to `@heidi/session/data` with the session-header modal cores.
// The `@domain REVIEW` tag was the hub's marker for "unclassified",
// and it resolves to session with the carve because the `ui--session-header`
// exception cones blocked on this read name that door by name. Re-exported for
// existing importers + `jest.mock` literals.
export { getSupportedLanguages } from '@heidi/session/data';

// Spec-derived: the hand-written type this replaces claimed an array, which
// inverted every successful upload into a failure.
type UploadAudioResponse =
  operations['upload_audio_api_v1_ml_scribe_sessions__session_id__upload_audio_post']['responses'][200]['content']['application/json'];

// Base per-attempt bound: connection setup plus the server's post-upload
// processing window, before the per-byte transfer allowance below.
const UPLOAD_AUDIO_BASE_TIMEOUT_MS = 10 * 60 * 1000;

// An axios timeout measures total request duration, not inactivity, so the
// bound must scale with the payload or it kills healthy-but-slow uploads.
// Below ~1 Mbps a whole-session upload is indistinguishable from a stall.
const UPLOAD_AUDIO_FLOOR_BYTES_PER_SECOND = 128 * 1024;

// Ceiling so a stalled giant upload still fails its attempt within the hour
// instead of scaling without bound.
const UPLOAD_AUDIO_MAX_TIMEOUT_MS = 60 * 60 * 1000;

const UPLOAD_AUDIO_RETRIES = 3;

const getUploadAudioTimeoutMs = (formData: FormData) => {
  let payloadBytes = 0;
  formData.forEach((value) => {
    if (value instanceof Blob) {
      payloadBytes += value.size;
    }
  });
  return Math.min(
    UPLOAD_AUDIO_MAX_TIMEOUT_MS,
    UPLOAD_AUDIO_BASE_TIMEOUT_MS +
      Math.ceil((payloadBytes / UPLOAD_AUDIO_FLOOR_BYTES_PER_SECOND) * 1000)
  );
};

type UploadProgressState = {
  uploadedBytes: number;
  totalBytes?: number;
};

/**
 * Retry only when the server cannot have processed the audio: the request got
 * no response and the body never finished sending, or the server answered 408
 * (timed out before reading the full request). Anything else is never
 * replayed — re-sending audio the server may already have persisted is what
 * leaves a session stuck in the Resume state with no way to upload again.
 *
 * Configs already retried by the Tier-1 auth handler are excluded: replaying
 * one makes the next 401 read as a definitive session-dead signal and
 * force-logs the user out.
 */
const shouldRetryAudioUpload = (
  sessionId: string,
  progress: UploadProgressState,
  error: AxiosError
) => {
  if (wasRetriedByAuthHandler(error)) {
    logger.warn('upload_audio_retry_suppressed_after_auth_retry', {
      sessionId,
      httpStatus: error.response?.status,
      axiosErrorCode: error.code,
    });
    return false;
  }
  if (error.response) {
    return error.response.status === 408;
  }
  // Request-interceptor failures such as confirmed desktop-offline never left the app.
  if (error.code === 'ERR_NETWORK' && error.request == null) {
    return false;
  }
  if (
    progress.totalBytes !== undefined &&
    progress.uploadedBytes >= progress.totalBytes
  ) {
    logger.warn('upload_audio_retry_suppressed_body_sent', {
      sessionId,
      axiosErrorCode: error.code,
    });
    return false;
  }
  // A timeout must retry: axios raises ECONNABORTED (or ETIMEDOUT under
  // `transitional.clarifyTimeoutError`), and axios-retry's network check
  // excludes ECONNABORTED, so relying on it alone would give up after the
  // first timed-out attempt.
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return true;
  }
  return isNetworkOrIdempotentRequestError(error);
};

/** @domain session */
export const uploadAudioFile = (
  sessionId: string,
  formData: FormData,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
) => {
  // How much of the body the current attempt has sent, so the retry condition
  // can refuse to replay audio that fully left the client.
  const progress: UploadProgressState = { uploadedBytes: 0 };
  return scribeHttp.post<UploadAudioResponse>(
    `/sessions/${sessionId}/upload-audio`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
      timeout: getUploadAudioTimeoutMs(formData),
      'axios-retry': {
        retries: UPLOAD_AUDIO_RETRIES,
        // The default treats timeout as one total budget, leaving no time for
        // another attempt after an Axios timeout consumes the full bound.
        shouldResetTimeout: true,
        retryCondition: (error: AxiosError) =>
          shouldRetryAudioUpload(sessionId, progress, error),
        onRetry: (retryCount: number, error: AxiosError) => {
          logger.warn('upload_audio_retry', {
            sessionId,
            retryCount,
            axiosErrorCode: error.code,
            httpStatus: error.response?.status,
          });
          progress.uploadedBytes = 0;
          progress.totalBytes = undefined;
        },
        // The rejection stays the raw AxiosError for the caller's toast; this
        // is the only error-level event keyed by sessionId, so an exhausted
        // upload leaves an attributable production trail.
        onMaxRetryTimesExceeded: (error: AxiosError) => {
          logger.error('upload_audio_retries_exhausted', {
            sessionId,
            axiosErrorCode: error.code,
            httpStatus: error.response?.status,
          });
        },
      },
      onUploadProgress: (progressEvent) => {
        progress.uploadedBytes = progressEvent.loaded;
        progress.totalBytes = progressEvent.total;
        if (progressEvent.total && onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    }
  );
};

// Carved to `@heidi/session/data` with the D2 transcript family (see above).
export { uploadBackupTranscript } from '@heidi/session/data';

/** @domain workspace */
export const submitIntegrationRequest = (input: IntegrationRequest) => {
  return scribeHttp.post<UserResponse>(`/user/submit-integration-request`, {
    integration: input.integration,
    ehr_provider: input.ehr_provider,
    email: input.email,
    other_integration: input.other_integration,
    is_clinician_decision_maker: input.is_clinician_decision_maker,
  });
};

/**
 * The residency region asserted by the access token the request would carry,
 * or `undefined` when the token is absent, unresolvable, or names a region
 * this client does not know.
 *
 * The token is the only signal allowed to pick a residency host — it comes
 * from the same credential the request presents, so it cannot route one
 * residency's identity at another's shard. Never falls back to a default, a
 * hostname, or the browser timezone; "no region" is a valid answer and
 * callers fail closed on it.
 */
const resolveAccessTokenRegion = async (): Promise<Region | undefined> => {
  let token: string | null | undefined;
  try {
    token = await getAuthTokenForHttpClient();
  } catch {
    return undefined;
  }
  if (!token) return undefined;
  const claimedRegion = extractRegionFromAccessToken(token);
  return Object.values(Region).find((known) => known === claimedRegion);
};

/** @domain workspace */
export const getKindeUserProperties = async () => {
  try {
    return await scribeHttp.get<{ properties: { key: string; value: string | null }[] }>(
      `/user/kinde-properties`
    );
  } catch (error: unknown) {
    // A transport error (no response) can mean the first attempt was aimed at
    // a host that never answered, so one retry is worth making — against the
    // token's own region only (see `resolveAccessTokenRegion`), never inferred.
    //
    // Getting that wrong is not just a failed retry: this call's response is
    // what the app resolves its region from, so a wrong host here becomes the
    // wrong host for everything after it. No known region, no retry.
    const axiosError = error as { isAxiosError?: boolean; response?: unknown };
    if (axiosError.isAxiosError && !axiosError.response) {
      const tokenRegion = await resolveAccessTokenRegion();
      if (!tokenRegion) throw error;
      const { getDomains } = await import('@/helpers/getDomains');
      // Use full URL to bypass the interceptor's baseURL override
      return scribeHttp.get<{ properties: { key: string; value: string | null }[] }>(
        `${getDomains(tokenRegion).baseV1Url}user/kinde-properties`
      );
    }
    throw error;
  }
};

/** @domain session */
export const getBackupAudioUrl = (sessionId: string, audio_format?: string) => {
  return scribeHttp.get<{ url: string }>(`/sessions/${sessionId}/backup-audio-url`, {
    params: { audio_format },
  });
};

/** @domain templates */
export const deleteTemplate = (templateId: string) => {
  return scribeHttp.delete<TemplateResponse>(`/template/${templateId}`);
};

/** @domain workspace */
export const logoutFromServer = () => {
  return scribeHttp.post('/user/logout');
};

/**
 * Revoke a specific session by explicitly passing its bearer token, instead of
 * the current Redux token. `/user/logout` is per-token / issuer-scoped, so
 * passing the OLD Kinde token routes to Kinde's revoke endpoint only.
 *
 * Used by the Kinde→Auth0 migration: Redux has already adopted the fresh Auth0
 * token by the time this runs, so we must carry the captured Kinde token
 * explicitly (`explicitKindeToken`) — the interceptor would otherwise attach
 * the Auth0 token. Revoking *after* the token swap is what keeps any request
 * from ever being sent with the revoked token. See `useKindeMigrationCompletion`.
 * @domain workspace
 */
export const revokeKindeSession = (kindeToken: string) => {
  return scribeHttp.post('/user/logout', null, { explicitKindeToken: kindeToken });
};

/** @domain workspace */
export const logoutEverywhere = () => {
  return scribeHttpV2.post<LogoutEverywhereResponse>(
    '/user/session-revocation/logout-everywhere'
  );
};

/** @domain workspace */
export const extendUnlimitedQuotas = (hours: number) => {
  return scribeHttp.post<UserResponse>('/user/extend-unlimit-quota', {
    extend_hours: hours,
  });
};

/** @domain workspace */
export const uploadProfileImage = (formData: FormData, signal?: AbortSignal) => {
  return scribeHttp.post<boolean>(`/user/profile-picture`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal,
  });
};

/** @domain workspace */
export const deleteProfileImage = () => {
  return scribeHttp.delete<boolean>(`/user/profile-picture`, {});
};

/** @domain session */
export const documentGenerateInsights = ({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId: string;
}) => {
  return scribeHttp.post<boolean>(
    `/sessions/${sessionId}/document/${documentId}/generate-insights`
  );
};

/** @domain REVIEW */
export const determineLogin = (email: string) => {
  return identityBrokerPublicHttp.post<DetermineLoginResponse>(`/determine-login`, {
    email,
  });
};

type CibaDeviceStatusResponse = {
  enabled: boolean;
  device_name: string | null;
};

type CibaEnrollmentTicketResponse = {
  ticket_url: string;
};

/** @domain REVIEW */
export const getCibaDeviceStatus = () => {
  return identityBrokerHttp.get<CibaDeviceStatusResponse>(`/ciba/device/status`);
};

/** @domain REVIEW */
export const createCibaEnrollmentTicket = () => {
  return identityBrokerHttp.post<CibaEnrollmentTicketResponse>(
    `/ciba/device/enroll-ticket`
  );
};

/** @domain REVIEW */
export const disableCibaDevice = () => {
  return identityBrokerHttp.delete(`/ciba/device/register`);
};

/** @domain REVIEW */
export const assignAppleMigrationTicket = () => {
  return identityBrokerHttp.post<AssignAppleMigrationTicketResponse>(
    `/assign-apple-migration-ticket`
  );
};

// --- Hardware Device ---

/** @domain REVIEW */
export const getHardwareDevice = (id: string) => {
  return scribeHttp.get<HardwareDeviceResponse>(
    `/hardware-device/${encodeURIComponent(id)}`
  );
};

/** @domain REVIEW */
export const linkHardwareDevice = (request: LinkHardwareDeviceRequest) => {
  return scribeHttp.post<HardwareDeviceResponse>('/hardware-device/', request);
};

/** @domain REVIEW */
export const unlinkHardwareDevice = (id: string) => {
  return scribeHttp.delete<UnlinkHardwareDeviceResponse>(
    `/hardware-device/${encodeURIComponent(id)}`
  );
};

/** @domain REVIEW */
export const listHardwareDevices = () => {
  return scribeHttp.get<HardwareDeviceResponse[]>('/hardware-device/');
};

/** @domain REVIEW */
export const patchHardwareDevice = (id: string, body: UpdateHardwareDeviceRequest) => {
  return scribeHttp.patch<HardwareDeviceResponse>(
    `/hardware-device/${encodeURIComponent(id)}`,
    body
  );
};

/** @domain dictation */
export const getDictationContext = (signal?: AbortSignal) => {
  return scribeHttpV2.get<DictationContextResponse>(
    `/dictation-post-processing/context`,
    { signal }
  );
};
